/**
 * Gestión de licencias: catálogo de recetas, tasas oficiales y pedidos.
 * El checkout cobra la tasa de la duración + el honorario de gestión.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  RefreshCw,
  Search,
  Loader2,
  FileText,
  Download,
} from 'lucide-react';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { exportLicenseAttributionCsv } from './exportLicenseAttribution';
import {
  useLicenseOrdersList,
  useLicenseOrdersStats,
} from './hooks/useLicenseOrders';
import { LicenseOrderDetailDialog } from './LicenseOrderDetailDialog';
import { LicenseOrderRowMenu } from './LicenseOrderRowMenu';
import { LicenseManualOrderDialog } from './LicenseManualOrderDialog';
import { LicenseRecipeManager } from './LicenseRecipeManager';
import { LicenseLeafletsPanel } from './LicenseLeafletsPanel';
import { NavigationOrdersPanel } from './NavigationOrdersPanel';
import { BluefinLeadsPanel } from './BluefinLeadsPanel';
import { InsuranceRequestsPanel } from './InsuranceRequestsPanel';
import {
  daysUntilReview,
  MILESTONE_LABEL,
  PRIORITY_LABEL,
  WAITING_ON_LABEL,
  WORKFLOW_STAGE_LABEL,
} from './opsLabels';
import type {
  LicenseOpsFilter,
  LicenseOrdersStats,
  LicenseOrderStatus,
  LicenseOrderStatusFilter,
  WorkflowStage,
} from './types';

const STATUS_TABS: { value: LicenseOrderStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'awaiting_review', label: 'Pendientes' },
  { value: 'in_progress', label: 'En curso' },
  { value: 'submitted', label: 'Presentadas' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'failed', label: 'Fallidos' },
  { value: 'refunded', label: 'Reembolsados' },
  { value: 'received', label: 'Sin pagar' },
  { value: 'abandoned', label: 'Abandonados' },
];

const OPS_TABS: {
  value: LicenseOpsFilter;
  label: string;
  statKey?: keyof LicenseOrdersStats;
}[] = [
  { value: 'all', label: 'Cola activa' },
  { value: 'waiting_us', label: 'Acción AiPeces', statKey: 'opsWaitingUs' },
  { value: 'waiting_alberto', label: 'Acción Alberto', statKey: 'opsWaitingAlberto' },
  { value: 'waiting_client', label: 'Espera cliente', statKey: 'opsWaitingClient' },
  { value: 'waiting_administration', label: 'Espera Admin.', statKey: 'opsWaitingAdministration' },
  { value: 'overdue', label: 'Vencidos', statKey: 'opsOverdue' },
  { value: 'ready_to_submit', label: 'Listos presentar', statKey: 'opsReadyToSubmit' },
  { value: 'license_received', label: 'Pend. entregar', statKey: 'opsLicenseReceived' },
  { value: 'blocked', label: 'Bloqueados', statKey: 'opsBlocked' },
  { value: 'delivered_ops', label: 'Entregados ops', statKey: 'opsDelivered' },
];

const STATUS_LABEL: Record<LicenseOrderStatus, string> = {
  received: 'Sin pagar',
  paid: 'Pagado',
  validated: 'Validado',
  in_progress: 'En curso',
  awaiting_review: 'Pendiente',
  awaiting_captcha: 'Captcha',
  awaiting_3ds: 'Espera 3DS',
  submitted: 'Presentada',
  delivered: 'Entregado',
  failed: 'Fallido',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado',
  abandoned: 'Abandonado',
};

function fmtEur(cents: number): string {
  return (
    ((cents || 0) / 100).toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '\u00a0€'
  );
}

function fmtDay(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-ES', { dateStyle: 'short' });
}

function AccountCell({
  registered,
  timing,
  registeredAt,
}: {
  registered: boolean | null;
  timing: 'before' | 'same_day' | 'after' | null;
  registeredAt: string | null;
}) {
  if (registered == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const tone = !registered
    ? 'text-rose-500'
    : timing === 'before'
      ? 'text-blue-400'
      : 'text-emerald-500';
  return (
    <div
      className={cn('leading-tight', tone)}
      title="Cuenta en la App AiPeces. No indica si la solicitud administrativa está presentada."
    >
      <div className="font-medium">{registered ? 'Registrado' : 'Sin cuenta'}</div>
      {registered && registeredAt ? <div className="text-xs opacity-80">{fmtDay(registeredAt)}</div> : null}
    </div>
  );
}

function paymentLabel(status: LicenseOrderStatus, paidAt: string | null, source: string | null): string {
  if (
    paidAt ||
    status === 'paid' ||
    status === 'awaiting_review' ||
    status === 'in_progress' ||
    status === 'submitted' ||
    status === 'delivered'
  ) {
    if (source === 'bizum') return 'Bizum';
    if (source === 'transfer') return 'Transfer.';
    if (source === 'manual') return 'Manual';
    if (source === 'stripe') return 'Stripe';
    return 'Pagado';
  }
  if (status === 'received') return 'Sin pagar';
  if (status === 'abandoned') return 'Abandonado';
  if (status === 'refunded') return 'Reembolsado';
  return STATUS_LABEL[status] || status;
}

function paymentTone(label: string): string {
  if (label === 'Stripe' || label === 'Pagado') return 'text-emerald-500 font-medium';
  if (label === 'Bizum' || label === 'Transfer.' || label === 'Manual') return 'text-blue-400 font-medium';
  if (label === 'Sin pagar') return 'text-rose-500 font-medium';
  return '';
}

function StatusBadge({ status }: { status: LicenseOrderStatus }) {
  const tone =
    status === 'awaiting_review' || status === 'awaiting_captcha' || status === 'awaiting_3ds'
      ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
      : status === 'submitted'
        ? 'bg-sky-500/10 text-sky-500 border-sky-500/30'
        : status === 'delivered'
        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
        : status === 'failed' || status === 'refunded'
          ? 'bg-rose-500/10 text-rose-500 border-rose-500/30'
          : status === 'abandoned'
            ? 'bg-slate-500/15 text-slate-300 border-slate-400/40'
            : 'bg-muted text-muted-foreground border-border';
  return (
    <span className={cn('inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium', tone)}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function stageTone(stage: WorkflowStage): string {
  switch (stage) {
    case 'new_order':
      return 'bg-sky-500/10 text-sky-400 border-sky-500/30';
    case 'collecting_data':
    case 'fee_pending':
    case 'requirement_received':
      return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
    case 'ready_to_submit':
    case 'fee_paid':
    case 'submitted':
      return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30';
    case 'awaiting_administration':
      return 'bg-violet-500/10 text-violet-400 border-violet-500/30';
    case 'license_received':
    case 'ready_to_deliver':
      return 'bg-teal-500/10 text-teal-400 border-teal-500/30';
    case 'delivered':
      return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
    case 'blocked':
      return 'bg-rose-500/10 text-rose-500 border-rose-500/30';
    case 'cancelled':
      return 'bg-muted text-muted-foreground border-border';
  }
}

function StageBadge({ stage }: { stage: WorkflowStage }) {
  return (
    <span className={cn('inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium', stageTone(stage))}>
      {WORKFLOW_STAGE_LABEL[stage] || stage}
    </span>
  );
}

export function LicensesPage() {
  const [tab, setTab] = useState<'products' | 'leaflets' | 'orders' | 'navigation' | 'bluefin' | 'insurance'>('orders');
  const [statusFilter, setStatusFilter] = useState<LicenseOrderStatusFilter>('all');
  const [opsFilter, setOpsFilter] = useState<LicenseOpsFilter>('waiting_us');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusProductChange, setFocusProductChange] = useState(false);
  const [rowNotice, setRowNotice] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [invoiceDownloadingId, setInvoiceDownloadingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { data: stats } = useLicenseOrdersStats();
  const { data: orders, isLoading, refetch, isFetching } = useLicenseOrdersList(
    statusFilter,
    search,
    opsFilter
  );

  const tabsWithCounts = useMemo(
    () =>
      STATUS_TABS.map((t) => {
        let count: number | undefined;
        if (!stats) count = undefined;
        else if (t.value === 'all') count = stats.totalOrders;
        else if (t.value === 'awaiting_review') count = stats.awaitingReview;
        else if (t.value === 'in_progress') count = stats.inProgress;
        else if (t.value === 'submitted') count = stats.submitted;
        else if (t.value === 'delivered') count = stats.delivered;
        else if (t.value === 'refunded') count = stats.refunded;
        else if (t.value === 'failed') count = stats.failed;
        else if (t.value === 'received') count = stats.received;
        else if (t.value === 'abandoned') count = stats.abandoned;
        return { ...t, count };
      }),
    [stats]
  );

  async function onExportAttribution() {
    setExporting(true);
    try {
      await exportLicenseAttributionCsv();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al exportar');
    } finally {
      setExporting(false);
    }
  }

  async function downloadOrderInvoice(orderId: string, invoiceId: string | null, invoiceNumber: string | null) {
    setInvoiceDownloadingId(orderId);
    try {
      await downloadLegalInvoicePdf(invoiceId || orderId, invoiceNumber ?? 'factura');
      void refetch();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al descargar la factura');
    } finally {
      setInvoiceDownloadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Licencias</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestiona el catálogo público, las tasas oficiales, los folletos y los pedidos tramitados.{' '}
            <Link
              to="/seo?tab=funnels&funnel=licenses_main_v1"
              className="text-primary hover:underline"
            >
              Embudo web GA4
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-muted/50 p-0.5">
            <button
              type="button"
              onClick={() => setTab('products')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'products' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Productos y precios
            </button>
            <button
              type="button"
              onClick={() => setTab('leaflets')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'leaflets' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Folletos
            </button>
            <button
              type="button"
              onClick={() => setTab('orders')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'orders' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Pedidos
              {typeof stats?.awaitingReview === 'number' && stats.awaitingReview > 0
                ? ` (${stats.awaitingReview})`
                : ''}
            </button>
            <button
              type="button"
              onClick={() => setTab('navigation')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'navigation' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Navegación CHE
            </button>
            <button
              type="button"
              onClick={() => setTab('bluefin')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'bluefin' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Atún
            </button>
            <button
              type="button"
              onClick={() => setTab('insurance')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md',
                tab === 'insurance' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
              )}
            >
              Seguros
            </button>
          </div>
          {tab === 'orders' && (
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:opacity-90"
            >
              Pedido manual
            </button>
          )}
          {tab === 'orders' && (
            <button
              type="button"
              onClick={() => void onExportAttribution()}
              disabled={exporting}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Exportar origen
            </button>
          )}
          {tab === 'orders' && (
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              Actualizar
            </button>
          )}
        </div>
      </div>

      {tab === 'products' ? (
        <LicenseRecipeManager />
      ) : tab === 'leaflets' ? (
        <LicenseLeafletsPanel />
      ) : tab === 'navigation' ? (
        <NavigationOrdersPanel />
      ) : tab === 'bluefin' ? (
        <BluefinLeadsPanel />
      ) : tab === 'insurance' ? (
        <InsuranceRequestsPanel />
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {OPS_TABS.map((t) => {
              const count =
                t.statKey && stats ? (stats[t.statKey] as number | null | undefined) : undefined;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setOpsFilter(t.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm border transition-colors',
                    opsFilter === t.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/40 text-muted-foreground border-border hover:text-foreground'
                  )}
                >
                  {t.label}
                  {typeof count === 'number' ? ` (${count})` : ''}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            {tabsWithCounts.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => {
                  setStatusFilter(t.value);
                  if (t.value === 'submitted' || t.value === 'abandoned') setOpsFilter('all');
                }}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  statusFilter === t.value
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
                )}
              >
                Pago: {t.label}
                {typeof t.count === 'number' ? ` (${t.count})` : ''}
              </button>
            ))}
          </div>

          <div className="relative max-w-lg">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pedido, email, teléfono, registro, soporte o últimos 4 del doc…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
            />
          </div>

          <div className="rounded-xl border border-border overflow-x-auto">
            {isLoading ? (
              <div className="p-10 flex justify-center">
                <Loader2 className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <table className="w-full text-sm min-w-[1100px]">
                <thead className="bg-muted/40 text-muted-foreground text-left">
                  <tr>
                    <th className="px-3 py-3 font-medium">Pedido / Cliente</th>
                    <th className="px-3 py-3 font-medium">Pago</th>
                    <th className="px-3 py-3 font-medium">Fase</th>
                    <th className="px-3 py-3 font-medium">Esperando</th>
                    <th className="px-3 py-3 font-medium">Hito</th>
                    <th className="px-3 py-3 font-medium">Siguiente acción</th>
                    <th className="px-3 py-3 font-medium">Revisión</th>
                    <th className="px-3 py-3 font-medium">Prioridad</th>
                    <th
                      className="px-3 py-3 font-medium"
                      title="Cuenta en la App AiPeces. No indica si la solicitud administrativa está presentada."
                    >
                      Cuenta App
                    </th>
                    <th className="px-3 py-3 font-medium text-center w-12">Factura</th>
                    <th className="px-3 py-3 font-medium text-center w-12">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(orders || []).map((o) => (
                    <tr
                      key={o.id}
                      onClick={() => {
                        setFocusProductChange(false);
                        setSelectedId(o.id);
                      }}
                      className={cn(
                        'border-t border-border cursor-pointer hover:bg-muted/30',
                        selectedId === o.id && 'bg-primary/5',
                        o.archivedAt && 'opacity-60'
                      )}
                    >
                      <td className="px-3 py-3">
                        <div className="font-medium">{o.publicId}</div>
                        <div className="text-xs text-muted-foreground">
                          {o.recipeName} · {o.contactEmail}
                          {o.contactPhone ? ` · ${o.contactPhone}` : ''}
                        </div>
                        <div className="text-[11px] text-muted-foreground/80">
                          {o.duration}
                          {o.assignedTo ? ` · ${o.assignedTo}` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className={paymentTone(paymentLabel(o.status, o.paidAt, o.paymentSource))}>
                          {paymentLabel(o.status, o.paidAt, o.paymentSource)}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {fmtEur(o.officialFeeCents + o.serviceFeeCents)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {o.workflowStage
                          ? <StageBadge stage={o.workflowStage} />
                          : <StatusBadge status={o.status} />}
                      </td>
                      <td className="px-3 py-3">
                        {o.waitingOn ? WAITING_ON_LABEL[o.waitingOn] : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {o.lastMilestone
                          ? MILESTONE_LABEL[o.lastMilestone] || o.lastMilestone
                          : '—'}
                      </td>
                      <td className="px-3 py-3 max-w-[220px] truncate" title={o.nextAction || ''}>
                        {o.nextAction || '—'}
                      </td>
                      <td className="px-3 py-3 tabular-nums text-xs">
                        <span
                          className={cn(
                            o.nextReviewAt && new Date(o.nextReviewAt).getTime() < Date.now()
                              ? 'text-rose-500 font-medium'
                              : 'text-muted-foreground'
                          )}
                        >
                          {daysUntilReview(o.nextReviewAt)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={cn(
                            o.priority === 'urgent' && 'text-amber-500 font-medium'
                          )}
                        >
                          {o.priority ? PRIORITY_LABEL[o.priority] : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <AccountCell
                          registered={o.accountRegistered}
                          timing={o.accountTiming}
                          registeredAt={o.accountRegisteredAt}
                        />
                      </td>
                      <td className="px-3 py-3 text-center">
                        {o.paidAt ? (
                          <button
                            type="button"
                            title={
                              o.legalInvoiceNumber
                                ? `Descargar ${o.legalInvoiceNumber}`
                                : 'Generar y descargar factura'
                            }
                            disabled={invoiceDownloadingId === o.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void downloadOrderInvoice(o.id, o.legalInvoiceId, o.legalInvoiceNumber);
                            }}
                            className="inline-flex items-center justify-center p-1.5 rounded-lg text-primary hover:bg-primary/10 disabled:opacity-50"
                          >
                            {invoiceDownloadingId === o.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <FileText size={16} />
                            )}
                          </button>
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center" onClick={(event) => event.stopPropagation()}>
                        <LicenseOrderRowMenu
                          order={o}
                          invoiceBusy={invoiceDownloadingId === o.id}
                          onOpen={() => {
                            setFocusProductChange(false);
                            setSelectedId(o.id);
                          }}
                          onChangeProduct={() => {
                            setFocusProductChange(true);
                            setSelectedId(o.id);
                          }}
                          onDownloadInvoice={() => {
                            void downloadOrderInvoice(o.id, o.legalInvoiceId, o.legalInvoiceNumber);
                          }}
                          onNotice={(text, tone) => setRowNotice({ text, tone })}
                        />
                      </td>
                    </tr>
                  ))}
                  {!orders?.length && (
                    <tr>
                      <td colSpan={11} className="px-4 py-10 text-center text-muted-foreground">
                        No hay pedidos en este filtro
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {rowNotice && (
            <p className={rowNotice.tone === 'error' ? 'text-sm text-rose-500' : 'text-sm text-emerald-600'}>
              {rowNotice.text}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Cola operativa: fase y «esperando a» son independientes del estado de pago.
            «Cuenta App» solo indica si el cliente tiene usuario en AiPeces, no si la solicitud
            está presentada. Orden: urgentes y vencidos primero.
          </p>

          <LicenseOrderDetailDialog
            orderId={selectedId}
            focusProductChange={focusProductChange}
            onClose={() => {
              setSelectedId(null);
              setFocusProductChange(false);
            }}
          />
          <LicenseManualOrderDialog
            open={manualOpen}
            onClose={() => setManualOpen(false)}
            onCreated={(id) => {
              setManualOpen(false);
              setFocusProductChange(false);
              setSelectedId(id);
              void refetch();
            }}
          />
        </>
      )}
    </div>
  );
}
